/**
 * Attachment domain schemas, derived from the generated Zod. `invoice`,
 * `ticket` and `documentReview` all embed these as the wire shape of a linked
 * file.
 *
 * `baseLinkedAttachmentSchema` has no generated counterpart: orval duplicates
 * the embedded `attachments[]` sub-object inside each consuming domain's file
 * rather than generating it once, so no single generated schema owns the shape.
 * It stays hand-declared as the shared, lenient base those domains extend.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import { AttachmentsCreateResponse } from '~/data/generated/zod/attachments/attachments.zod';
import { TicketsAttachmentsListResponse } from '~/data/generated/zod/tickets/tickets.zod';

import { type OneOrMore, type Values } from './zodHelpers';

export const AttachmentType = {
  Pdf: 'pdf',
  Image: 'image',
  Spreadsheet: 'spreadsheet',
};
type AttachmentTypeKey = Values<typeof AttachmentType>;
const AttachmentTypeValues = Object.values(AttachmentType) as OneOrMore<AttachmentTypeKey>;

// Callers hand this a plain string needing narrowing, not an already-validated
// enum member — hence the coercion rather than the generated bare enum.
const fileTypeSchema = z.string().pipe(z.enum(AttachmentTypeValues));

/** The linked-attachment sub-object embedded in ticket/invoice/contract reads. */
export const baseLinkedAttachmentSchema = z.object({
  id: z.string(),
  attachmentId: z.string(),
  // Wire emits null (the url getter is `str | None` server-side).
  attachmentUrl: z.string().nullable(),
  fileType: fileTypeSchema,
  // Wire emits null for any non-PDF ("Only for PDFs" — the column is null=True),
  // so a photo attachment throws if this is a required number.
  pageNumber: z.number().nullable(),
  isPrimary: z.boolean(),
  isNextPage: z.boolean(),
});
export type BaseLinkedAttachment = z.infer<typeof baseLinkedAttachmentSchema>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const linkedPDFAttachmentSchema = baseLinkedAttachmentSchema
  .omit({ pageNumber: true })
  .extend({ pageNumber: z.number() });
type LinkedPDFAttachment = z.infer<typeof linkedPDFAttachmentSchema>;

/** isLinkedPDFAttachment guard return whether attachment is a PDF or not */
export function isLinkedPDFAttachment(
  attachment: BaseLinkedAttachment,
): attachment is LinkedPDFAttachment {
  return attachment.fileType === AttachmentType.Pdf;
}

/**
 * One page of a record's original document, matching how the API itself defines
 * that set: `(is_primary or is_next_page) and not is_void`, per
 * `_sort_invoice_attachments` in `main/lib/invoice/stamped_pdf.py`.
 *
 * Two writers produce these rows and they do not agree on the flags. The document
 * pipeline writes page 1 as `is_primary` alone and pages 2..n as `is_next_page`
 * alone (`main/documents/approval.py:_link_attachments`), while
 * `SupportingDataForm` writes continuation pages with BOTH set. `isVoid` marks the
 * pages a re-upload superseded.
 *
 * Filtering on `isPrimary` alone therefore dropped every pipeline-written
 * continuation page (AMP-722). Callers that bucket a record's pages into
 * "the original document" and "everything else" should use this and its negation,
 * so the two buckets stay exact complements.
 */
export const isOriginalDocumentPage = (item: {
  isPrimary: boolean;
  isNextPage: boolean;
  isVoid?: boolean;
}) => (item.isPrimary || item.isNextPage) && !item.isVoid;

/** Ascending page order within a source file; unpaged rows (non-PDFs) sort first. */
export const byPageNumber = (
  prev: { pageNumber: number | null },
  curr: { pageNumber: number | null },
) => (prev.pageNumber ?? 0) - (curr.pageNumber ?? 0);

/** The standalone Attachment object, reused as the lenient embedded shape. */
export const attachmentSchema = AttachmentsCreateResponse.extend({
  // Loosened from the generated `.uuid()`/`.url()`: real embedded payloads and
  // fixtures carry synthetic ids and relative media paths, not absolute URLs.
  id: z.string(),
  file: z.string(),
  fileType: fileTypeSchema,
});
export type Attachment = z.infer<typeof attachmentSchema>;

/**
 * `GET /tickets/{ticketPk}/attachments/` — no override needed.
 *
 * Do NOT widen `isPrimary`/`isNextPage` to `.nullable()`: the wire always emits
 * a real boolean, and the generated `.optional()` is already right.
 */
export const ticketAttachmentsListSchema = TicketsAttachmentsListResponse;
